import * as React from "react";
import {
  PieChart as PieChartIcon,
  LayoutList,
} from "lucide-react";
import { Pie, PieChart, Cell, ResponsiveContainer } from "recharts";
import { cn } from "@/lib/utils";
import { CHART_COLORS } from "@/lib/chart-colors";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import type { PerformanceData } from "@/schemas";
import { useBaseCurrency } from "@/hooks/use-base-currency";
import { AssetAllocationSettingsModal } from "./AssetAllocationSettingsModal";

interface AssetAllocationChartProps {
  performanceData: PerformanceData | null;
  className?: string;
}

type GroupByType = "asset" | "type";

export function AssetAllocationChart({
  performanceData,
  className,
}: AssetAllocationChartProps) {
  const [groupBy, setGroupBy] = React.useState<GroupByType>("asset");
  const [includeCash, setIncludeCash] = React.useState(false);
  const { code: currencyCode } = useBaseCurrency();

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const formatCompactCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  };

  const { chartData, chartConfig, totalValue, assetCount } =
    React.useMemo(() => {
      if (!performanceData) {
        return {
          chartData: [],
          chartConfig: {} as ChartConfig,
          totalValue: 0,
          assetCount: 0,
        };
      }

      const assets = performanceData.assets_performance;

      if (!assets || Object.keys(assets).length === 0) {
        return {
          chartData: [],
          chartConfig: {} as ChartConfig,
          totalValue: 0,
          assetCount: 0,
        };
      }

      const positiveValueEntries = Object.entries(assets).filter(([, asset]) => {
        const hasValue = (asset.current_value || 0) > 0;
        const isCash = asset.asset_type === "fiat";

        if (!hasValue) {
          return false;
        }

        if (!includeCash && isCash) {
          return false;
        }

        return true;
      });
      const positiveValueAssets = positiveValueEntries.map(([, asset]) => asset);
      const count = positiveValueAssets.length;

      const calculatedTotal =
        positiveValueAssets.length > 0
          ? positiveValueAssets.reduce(
              (sum, asset) => sum + (asset.current_value || 0),
              0,
            )
          : 0;

      if (calculatedTotal <= 0) {
        return {
          chartData: [],
          chartConfig: {} as ChartConfig,
          totalValue: 0,
          assetCount: count,
        };
      }

      const finalTotal = calculatedTotal;

      const typeLabels: Record<string, string> = {
        stock: "Stocks",
        crypto: "Crypto",
        etf: "ETFs",
        fiat: "Cash",
        index: "Indices",
        other: "Other",
      };

      const data: Array<{
        name: string;
        key: string;
        value: number;
        percentage: number;
        color: string;
      }> = [];
      const config: ChartConfig = {
        value: { label: "Value" },
      };

      let groupCount = count;

      if (groupBy === "type") {
        const typeGroups: Record<string, number> = {};

        positiveValueAssets.forEach((asset) => {
          const type = asset.asset_type || "other";
          typeGroups[type] =
            (typeGroups[type] || 0) + (asset.current_value || 0);
        });

        const sortedTypeGroups = Object.entries(typeGroups)
          .filter(([, value]) => value > 0)
          .sort(([, a], [, b]) => b - a);

        groupCount = sortedTypeGroups.length;

        sortedTypeGroups.forEach(([type, value], index) => {
          const color = CHART_COLORS[index % CHART_COLORS.length];
          data.push({
            name: typeLabels[type] || type,
            key: type,
            value,
            percentage: (value / finalTotal) * 100,
            color,
          });
          config[type] = {
            label: typeLabels[type] || type,
            color,
          };
        });
      } else {
        const assetGroups = new Map<string, number>();
        positiveValueEntries.forEach(([assetId, asset]) => {
          const name = asset.asset_name || asset.asset_symbol || assetId;
          assetGroups.set(
            name,
            (assetGroups.get(name) || 0) + (asset.current_value || 0),
          );
        });

        groupCount = assetGroups.size;

        Array.from(assetGroups.entries())
          .sort(([, a], [, b]) => b - a)
          .forEach(([name, value], index) => {
            const color = CHART_COLORS[index % CHART_COLORS.length];
            data.push({
              name,
              key: name,
              value,
              percentage: (value / finalTotal) * 100,
              color,
            });
            config[name] = {
              label: name,
              color,
            };
          });
      }

      return {
        chartData: data,
        chartConfig: config,
        totalValue: finalTotal,
        assetCount: groupCount,
      };
    }, [performanceData, groupBy, includeCash]);

  if (!chartData.length) {
    return (
      <div
        className={cn(
          "flex items-center justify-center h-full w-full text-muted-foreground text-sm",
          className,
        )}
      >
        No allocation data
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col h-full w-full", className)}>
      {/* Header with toggle */}
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">
            Allocation
          </span>
          <div className="flex items-center bg-muted/50 rounded-md p-0.5 ml-2">
            <button
              onClick={() => setGroupBy("asset")}
              className={cn(
                "flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-all",
                groupBy === "asset"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <PieChartIcon className="h-3 w-3" />
              Asset
            </button>
            <button
              onClick={() => setGroupBy("type")}
              className={cn(
                "flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-all",
                groupBy === "type"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <LayoutList className="h-3 w-3" />
              Type
            </button>
          </div>
        </div>
        <AssetAllocationSettingsModal
          includeCash={includeCash}
          onIncludeCashChange={setIncludeCash}
        />
      </div>

      {/* Content: Chart + Legend */}
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-8">
        {/* Chart */}
        <div className="relative flex-1 w-full max-w-[320px] lg:max-w-[400px] aspect-square p-4 lg:p-8">
          {/* Center label */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-2xl font-semibold tracking-tight">
              {formatCompactCurrency(totalValue)}
            </span>
            <span className="text-xs text-muted-foreground">
              {assetCount} {groupBy === "type" ? "types" : "assets"}
            </span>
          </div>
          <ChartContainer
            config={chartConfig}
            className="w-full h-full relative z-10"
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      hideLabel
                      formatter={(value, name, item) => {
                        const percentage = item.payload?.percentage ?? 0;
                        return (
                          <div className="flex flex-col gap-1 min-w-[180px]">
                            <span className="text-sm font-semibold">
                              {name}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-semibold text-sm">
                                {formatCurrency(value as number)}
                              </span>
                              <span className="text-muted-foreground text-sm">
                                {percentage.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                        );
                      }}
                    />
                  }
                />
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="60%"
                  outerRadius="90%"
                  strokeWidth={2}
                  stroke="hsl(var(--background))"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </div>

        {/* Legend List - Shown only on larger screens as requested */}
        <div className="hidden lg:block min-w-[140px] max-w-[200px] min-h-0 overflow-auto flex-shrink-0">
          <div className="flex flex-col gap-2">
            {chartData.map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <div className="flex flex-col">
                  <span className="font-semibold text-foreground">
                    {item.name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {item.percentage.toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Export legacy components for backward compatibility
export { AssetAllocationChart as AssetAllocationList };
